// src/app/core/auth.service.ts
import {Injectable, inject} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, map} from 'rxjs';

import {ApiConfiguration} from '../../api/api-configuration';
import {loginPost} from '../../api/fn/login/login-post';
import {LoginPostRequest, LoginPost200Response, AuthResetPasswordPostRequest} from '../../api/models';
import {authResetPasswordPost} from "../../api/fn/auth/auth-reset-password-post";

export type UserRole = 'user' | 'admin' | 'superAdmin';

@Injectable({providedIn: 'root'})
export class AuthService {
    private readonly http = inject(HttpClient);
    private readonly cfg = inject(ApiConfiguration);

    private readonly tokenKey = 'access_token';

    private _token$ = new BehaviorSubject<string | null>(this.readToken());
    /** Observe auth changes if needed */
    readonly token$ = this._token$.asObservable();

    login(email: string, password: string) {
        const body: LoginPostRequest = {
            email,
            password,
        };

        return loginPost(this.http, this.cfg.rootUrl, {body}).pipe(
            map((resp) => {
                // resp is StrictHttpResponse<LoginPost200Response>
                const tokenPayload = resp.body as LoginPost200Response;

                // Adjust to your response model keys:
                const accessToken = (tokenPayload as any).accessToken ?? (tokenPayload as any).token;
                if (accessToken) {
                    localStorage.setItem(this.tokenKey, accessToken);
                    this._token$.next(accessToken);
                }
                const refreshToken = (tokenPayload as any).refreshToken;
                if (refreshToken) {
                    localStorage.setItem('refresh_token', refreshToken);
                }
                localStorage.setItem('organizationIds', JSON.stringify(resp.body.user?.organizationIds))
                localStorage.setItem('role', JSON.stringify(resp.body.user?.role))
                localStorage.setItem('userName', JSON.stringify(resp.body.user?.firstName))
                localStorage.setItem('email', JSON.stringify(resp.body.user?.email))
                return tokenPayload;
            })
        );
    }

    logout() {
        localStorage.removeItem(this.tokenKey);
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('organizationIds');
        localStorage.removeItem('organization');
        localStorage.removeItem('role');
        localStorage.removeItem('userName');
        localStorage.removeItem('email');
        this._token$.next(null);
    }

    resetPassword(currentPassword: string, newPassword: string) {
        const body: AuthResetPasswordPostRequest = {
            currentPassword: currentPassword,
            newPassword: newPassword
        }

        return authResetPasswordPost(this.http, this.cfg.rootUrl, {body}).pipe(
            map(r => r.body)
        )
    }

    get token(): string | null {
        return localStorage.getItem(this.tokenKey);
    }

    private readToken(): string | null {
        return localStorage.getItem(this.tokenKey);
    }

    isAuthenticated(): boolean {
        const t = this.token;
        if (!t) return false;
        const payload = this.decodeJwt(t);
        if (!payload) return false;

        // validate exp if present
        if (typeof payload['exp'] === 'number') {
            const now = Date.now() / 1000;
            if (payload['exp'] < now) return false;
        }
        return true;
    }

    getRoles(): UserRole[] {

        const persisted = localStorage.getItem('role');
        if (persisted) {
            try {
                const r = JSON.parse(persisted);
                return Array.isArray(r) ? r : [r];
            } catch {

            }
        }

        // Priority 2: derive from JWT claims if available
        const t = this.token;
        if (!t) return [];
        const claims: any = this.decodeJwt(t);
        if (Array.isArray(claims?.roles)) return claims.roles;
        if (typeof claims?.role === 'string') return [claims.role];

        // Example: map OAuth scopes to coarse roles
        if (typeof claims?.scope === 'string') {
            const scopes: string[] = claims.scope.split(/\s+/);
            if (scopes.some(s => s.endsWith('.write'))) return ['admin'];
            return ['user'];
        }

        return [];
    }

    getRole(): UserRole | null {
        const role = this.readJson<UserRole | UserRole[]>('role');
        if (Array.isArray(role)) {
            return role[0] ?? null;
        }

        return role ?? null;
    }

    getOrganizationIds(): string[] {
        const organizationIds = this.readJson<string[] | string>('organizationIds');
        if (Array.isArray(organizationIds)) {
            return organizationIds;
        }

        return organizationIds ? [organizationIds] : [];
    }

    getUserName(): string | null {
        const userName = this.readJson<string>('userName');
        return typeof userName === 'string' ? userName : "Hello";
    }

    getEmail(): string | null {
        const email = this.readJson<string>('email');
        return typeof email === 'string' ? email : null;
    }

    hasAnyRole(required?: UserRole[]): boolean {
        if (!required || required.length === 0) return true;
        const mine = this.getRoles();
        return required.some(r => mine.includes(r));
    }

    private decodeJwt(token: string): Record<string, any> | null {
        try {
            const payload = token.split('.')[1];
            return JSON.parse(atob(payload));
        } catch {
            return null;
        }
    }

    private readJson<T>(key: string): T | null {
        const rawValue = localStorage.getItem(key);
        if (rawValue == null) {
            return null;
        }

        try {
            return JSON.parse(rawValue) as T;
        } catch {
            return null;
        }
    }
}
